/**
 * DenyTermsEditor: the customer's client names, one per row. The Eval PRs
 * section and the Add benchmark dialog both edit the row's
 * `standing_deny_terms` with it.
 */

import { Plus, X } from 'lucide-react'

import { RowAction } from '@/components/settings/RowAction'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

/**
 * Plain strings, so the row is an input and a remove button rather than
 * its own component.
 */
export function DenyTermsEditor({
  terms,
  onChange,
  disabled = false,
}: {
  terms: string[]
  onChange: (next: (previous: string[]) => string[]) => void
  disabled?: boolean
}): React.JSX.Element {
  return (
    <div className="max-w-xl space-y-3">
      {terms.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No client names will be scrubbed.
        </p>
      ) : (
        <ul className="space-y-2">
          {terms.map((term, index) => (
            // Rows are appended and removed, never reordered.
            <li key={index} className="flex items-center gap-2">
              <Input
                value={term}
                autoComplete="off"
                disabled={disabled}
                aria-label={`Deny term ${String(index + 1)}`}
                placeholder="Northway Logistics"
                onChange={(e) => {
                  const next = e.target.value
                  onChange((previous) =>
                    previous.map((row, i) => (i === index ? next : row)),
                  )
                }}
              />
              <RowAction
                label={`Remove deny term ${String(index + 1)}`}
                icon={X}
                destructive
                disabled={disabled}
                onClick={() => {
                  onChange((previous) => previous.filter((_row, i) => i !== index))
                }}
              />
            </li>
          ))}
        </ul>
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled}
        onClick={() => {
          onChange((previous) => [...previous, ''])
        }}
      >
        <Plus aria-hidden="true" />
        Add client name
      </Button>
    </div>
  )
}
